import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Send, Trash2, UserPlus, Users, X } from "lucide-react";
import { ConfirmDelete } from "./ConfirmDelete";
import { api, formatApiError } from "../lib/api";

const input = "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20";
const HELP = "Co-hosts can edit the invite, see responses, manage the guest list and check guests in. Only you can delete the event or change co-hosts.";

const STATUS_CLS = { active: "bg-emerald-50 text-emerald-700", pending: "bg-amber-50 text-amber-700", expired: "bg-slate-100 text-slate-600", declined: "bg-red-50 text-red-700" };
const smallBtn = "inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60";

const CohostRow = ({ c, owner, busy, onRemove, onResend }) => (
  <li data-testid={`cohost-row-${c.id}`} className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0">
    <div className="min-w-0 flex-1 basis-48">
      <p data-testid={`cohost-email-${c.id}`} className="truncate font-semibold text-slate-900">{c.name || c.email}{c.is_me && " (you)"}</p>
      {c.name && <p className="truncate text-xs text-slate-500">{c.email}</p>}
    </div>
    <span data-testid={`cohost-status-${c.id}`} className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_CLS[c.status]}`}>
      {c.status_label}
    </span>
    {owner && c.status !== "active" && (
      <button data-testid={`cohost-resend-btn-${c.id}`} disabled={busy} onClick={() => onResend(c)} className={smallBtn}><Send className="h-3.5 w-3.5" /> Resend invite</button>
    )}
    {!owner && c.is_me && (
      <ConfirmDelete testid="cohost-leave" busy={busy} onConfirm={() => onRemove(c)} title="Leave this event?"
        description="You'll lose access right away. The owner can invite you again." confirmLabel="Leave event" icon={LogOut}>
        <button data-testid="cohost-leave-btn" className={`${smallBtn} text-red-600 hover:bg-red-50`}><LogOut className="h-3.5 w-3.5" /> Leave event</button>
      </ConfirmDelete>
    )}
    {owner && (
      <ConfirmDelete testid={`cohost-remove-${c.id}`} busy={busy} onConfirm={() => onRemove(c)}
        title={c.status === "active" ? `Remove ${c.email} as co-host?` : `Cancel the invite for ${c.email}?`}
        description={c.status === "active" ? "They lose access to this event right away." : "The invite link stops working. You can invite them again later."}
        confirmLabel={c.status === "active" ? "Remove" : "Cancel invite"} cancelLabel={c.status === "active" ? "Cancel" : "Keep invite"}>
        <button data-testid={`cohost-remove-btn-${c.id}`} className={`${smallBtn} text-red-600 hover:bg-red-50`}>
          {c.status === "active" ? <><Trash2 className="h-3.5 w-3.5" /> Remove</> : <><X className="h-3.5 w-3.5" /> Cancel invite</>}
        </button>
      </ConfirmDelete>
    )}
  </li>
);

export const CohostSection = ({ eventId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/events/${eventId}/cohosts`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;
  const owner = data.role === "owner";

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Enter an email address");
    setBusy(true);
    try {
      const { data: c } = await api.post(`/events/${eventId}/cohosts`, { email });
      toast.success(`Invite sent to ${c.email}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (c) => {
    setBusy(true);
    try {
      await api.delete(`/events/${eventId}/cohosts/${c.id}`);
      if (c.is_me) {
        toast.success("You left this event");
        return navigate("/dashboard");
      }
      toast.success(c.status === "active" ? `Removed ${c.email}` : `Invite for ${c.email} cancelled`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const resend = async (c) => {
    setBusy(true);
    try {
      await api.post(`/events/${eventId}/cohosts/${c.id}/resend`);
      toast.success(`New invite sent to ${c.email}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="cohost-section" className="mt-12 rounded-3xl border border-slate-200 bg-white p-5 sm:p-8">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700">Team</p>
      <h2 className="font-display mt-1 flex items-center gap-2 text-2xl font-extrabold tracking-tight text-slate-900"><Users className="h-6 w-6 text-blue-700" /> Co-hosts</h2>
      <p data-testid="cohost-help" className="mt-1 max-w-2xl text-sm text-slate-500">
        {owner ? HELP : `You're a co-host. ${data.owner_name} owns this event and manages co-hosts.`}
      </p>
      {owner && (
        <form onSubmit={add} noValidate data-testid="cohost-add-form" className="mt-6 flex flex-col gap-2 sm:flex-row">
          <input data-testid="cohost-email-input" type="email" inputMode="email" autoCapitalize="none" autoComplete="off" className={`${input} sm:flex-1`} maxLength={254} placeholder="Co-host's email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button type="submit" data-testid="cohost-add-btn" disabled={busy} className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 text-sm font-semibold text-white disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add co-host
          </button>
        </form>
      )}
      <ul data-testid="cohost-list" className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
        <li data-testid="cohost-owner-row" className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0 flex-1 basis-48">
            <p data-testid="cohost-owner-name" className="truncate font-semibold text-slate-900">{data.owner_name}{owner && " (you)"}</p>
            <p className="truncate text-xs text-slate-500">{data.owner_email}</p>
          </div>
          <span className="shrink-0 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700">Owner</span>
        </li>
        {data.cohosts.map((c) => <CohostRow key={c.id} c={c} owner={owner} busy={busy} onRemove={remove} onResend={resend} />)}
      </ul>
      {data.cohosts.length === 0 && (
        <p data-testid="cohost-empty" className="mt-3 rounded-xl bg-slate-50 px-4 py-4 text-center text-sm text-slate-500">No co-hosts yet.</p>
      )}
    </section>
  );
};
